import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

function PendingProposalsSection({ pendingProposals, onReviewClick }) {
  const { t } = useTranslation();
  
  if (pendingProposals.length === 0) {
    return null;
  }
  
  return (
    <Card sx={{ mb: 3, border: '2px solid', borderColor: 'warning.light' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <InfoIcon sx={{ color: 'warning.main', mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('observations.pendingReviews', 'Pending Reviews')} ({pendingProposals.length})
          </Typography>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('observations.pendingReviewsDescription', 'AI has extracted new data points that need your review before being added to observations.')}
        </Alert>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                <TableCell><strong>{t('observations.date')}</strong></TableCell>
                <TableCell><strong>{t('observations.indicatorId')}</strong></TableCell>
                <TableCell align="right"><strong>{t('observations.extractedValue', 'Extracted Value')}</strong></TableCell>
                <TableCell align="right"><strong>{t('observations.currentValue', 'Current Value')}</strong></TableCell>
                <TableCell><strong>{t('observations.confidence', 'Confidence')}</strong></TableCell>
                <TableCell><strong>{t('observations.source', 'Source')}</strong></TableCell>
                <TableCell align="center"><strong>{t('observations.review', 'Review')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingProposals.slice(0, 5).map((proposal) => (
                <TableRow key={proposal.id} hover>
                  <TableCell>
                    <Chip label={proposal.observation_date} size="small" color="primary" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {proposal.indicator_id}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {proposal.extracted_value?.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {proposal.current_value !== null ? (
                      <Box>
                        <Typography variant="body2">
                          {proposal.current_value?.toFixed(2)}
                        </Typography>
                        <Chip 
                          label={t('observations.willOverwrite', 'Will overwrite')} 
                          size="small" 
                          color="warning" 
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${(proposal.confidence * 100).toFixed(0)}%`}
                      size="small"
                      color={proposal.confidence >= 0.9 ? 'success' : proposal.confidence >= 0.7 ? 'warning' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={proposal.source_text || ''}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 150, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'help'
                        }}
                      >
                        "{proposal.source_text}"
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title={t('observations.approve', 'Approve')}>
                        <IconButton 
                          size="small" 
                          color="success"
                          onClick={() => onReviewClick(proposal, 'approve')}
                        >
                          <ApproveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('observations.reject', 'Reject')}>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => onReviewClick(proposal, 'reject')}
                        >
                          <RejectIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {pendingProposals.length > 5 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            {t('observations.showingFirst', 'Showing first')} 5 {t('observations.of', 'of')} {pendingProposals.length} {t('observations.pendingProposals', 'pending proposals')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default PendingProposalsSection;
